import { Button as AntButton } from 'antd';
import styled from 'styled-components';
import { ButtonType } from '../../constants/style';
import { mediaQuery } from '../../styles/styled';

export interface IButton {
  $type: ButtonType;
  as: React.ReactElement;
  $fullWidth: boolean;
}

export const getTypedStyle = (type: ButtonType) =>
  ({
    [ButtonType.PRIMARY]: {
      color: 'var(--color-white)',
      backgroundColor: 'var(--color-primary)',
      border: '1px solid transparent',

      hover: {
        color: 'var(--color-primary)',
        backgroundColor: 'var(--color-white)',
        border: '1px solid var(--color-primary)',
      },
      disabled: {
        backgroundColor: 'var(--color-primary-disabled)',
        color: 'var(--color-white)',
        border: 'none',
      },
    },
    [ButtonType.GHOST]: {
      color: 'var(--color-primary)',
      backgroundColor: 'transparent',
      border: '1px solid transparent',

      hover: {
        color: 'var(--color-primary)',
        backgroundColor: 'var(--color-white)',
        border: '1px solid var(--color-primary)',
      },
      disabled: {
        backgroundColor: 'transparent',
        color: 'var(--color-primary-disabled)',
        border: 'none',
      },
    },
    [ButtonType.OUTLINE]: {
      color: 'var(--color-black-bolder)',
      backgroundColor: 'var(--color-white)',
      border: '1px solid var(--color-border-light)',

      hover: {
        color: 'var(--color-primary)',
        backgroundColor: 'var(--color-white)',
        border: '1px solid var(--color-primary)',
      },
      disabled: {
        backgroundColor: 'var(--color-white)',
        color: 'var(--color-primary-disabled)',
        border: '1px solid var(--color-primary-disabled)',
      },
    },
    [ButtonType.TRANSPARENT]: {
      color: 'var(--color-primary)',
      backgroundColor: 'transparent',
      border: '1px solid var(--color-primary)',

      hover: {
        color: 'var(--color-primary)',
        backgroundColor: 'var(--color-white)',
        border: '1px solid var(--color-primary)',
      },
      disabled: {
        backgroundColor: 'var(--color-primary-disabled)',
        color: 'var(--color-white)',
        border: '1px solid var(--color-primary)',
      },
    },
    [ButtonType.DANGER]: {
      color: '#b42318',
      backgroundColor: 'transparent',
      border: '1px solid #b42318 !important',
      hover: {
        color: 'var(--color-white) !important',
        backgroundColor: '#b42318',
        border: '1px solid #b42318 !important',
      },
      disabled: {
        color: 'var(--color-white) !important',
        backgroundColor: '#b42318',
        border: '1px solid #b42318 !important',
      },
    },
  }[type]);

export const Button = styled(AntButton)<IButton>`
  height: 36px;
  border-radius: 8px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  transition: all 0.3s ease;
  white-space: nowrap;
  cursor: pointer;

  ::after {
    display: none;
  }

  ${mediaQuery.md`
    height: 35px;
  `};

  && {
    border-radius: 8px;
    ${({ $fullWidth }) => $fullWidth && `width: 100%;`}
  }

  ${({ $type }) =>
    $type === ButtonType.GHOST &&
    `
  box-shadow: none;
  `}

  ${mediaQuery.md`
    ${({ $type }: { $type: ButtonType }) => {
      const styles = getTypedStyle($type);

      return `
        &&:hover {
          background-color: ${styles.hover.backgroundColor};
          color: ${styles.hover.color};
          border: ${styles.hover.border};

          i {
            color: ${styles.hover.color};
          }
        }
      `;
    }}
  `}

  ${({ $type }) => {
    const styles = getTypedStyle($type);
    const primaryBtnStyles = getTypedStyle(ButtonType.PRIMARY);

    return `
      && {
        background-color: ${styles.backgroundColor};
        color: ${styles.color};
        border: ${styles.border};

        i {
          color: ${styles.color};
        }
      }

      &&:focus {
        background-color: ${styles.backgroundColor};
        color: ${styles.color};
        border: ${styles.border};

        i {
          color: ${styles.color};
        }
      }

      &&:disabled {
        background-color: ${styles.disabled.backgroundColor};
        color: ${styles.disabled.color};
        border: ${styles.disabled.border};
        pointer-events: none;
        span > i {
            color: ${styles.disabled.color};
        }
      }

      &&.selected {
        color: ${primaryBtnStyles.color};
        background-color: ${primaryBtnStyles.backgroundColor};

        i {
          color: ${primaryBtnStyles.color};
        }
      }
    `;
  }}
`;

export const LinkButton = styled.a<{
  $type?: ButtonType;
  $disabled?: boolean;
}>`
  height: 35px;
  border-radius: 35px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-white);
  transition: all 0.3s ease;
  white-space: nowrap;

  ::after {
    display: none;
  }

  ${({ $type = ButtonType.PRIMARY, $disabled = false }) => {
    const styles = getTypedStyle($type);
    const btnStyles = getTypedStyle(
      $type === ButtonType.PRIMARY ? ButtonType.OUTLINE : ButtonType.PRIMARY
    );

    return `
      background-color: ${styles.backgroundColor};
      color: ${styles.color};
      border: ${styles.border || 'none'};
      ${
        $disabled
          ? `
        cursor: not-allowed;
        pointer-events: none;
        background-color: ${styles.disabled.backgroundColor};
        color: ${styles.disabled.color};
        border: ${styles.disabled.border};
        &:hover {
          color: ${styles.disabled.color};
        }
      `
          : `
        &:hover {
          background-color: ${btnStyles.backgroundColor};
          color: ${btnStyles.color};
          border: ${btnStyles.border};
        }
      `
      }
    `;
  }}
`;

export const ExternalLinkButton = styled.a<{
  $type?: ButtonType;
  $disabled?: boolean;
}>`
  height: 35px;
  border-radius: 35px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-white);
  transition: all 0.3s ease;
  white-space: nowrap;
  margin: 0 5px;

  ::after {
    display: none;
  }

  ${({ $type = ButtonType.PRIMARY, $disabled = false }) => {
    const styles = getTypedStyle($type);
    return `
      background-color: ${styles.backgroundColor};
      color: ${styles.color};
      border: ${styles.border || 'none'};

      ${
        $disabled
          ? `
        cursor: not-allowed;
        pointer-events: none;
        background-color: ${styles.disabled.backgroundColor};
        color: ${styles.disabled.color};
        border: ${styles.disabled.border};
        &:hover {
          color: ${styles.disabled.color};
        }
      `
          : `
        &:hover {
          background-color: ${styles.hover.backgroundColor};
          color: ${styles.hover.color};
          border: ${styles.hover.border};
        }
      `
      }
    `;
  }}
`;

export const IconSwitchButton = styled(AntButton)<{
  $icons: [string, string];
  $selected?: boolean;
  $disabled?: boolean;
}>`
  &&& {
    width: 35px;
    height: 35px;
    border-radius: 35px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    white-space: nowrap;
    box-shadow: none;
    font-family: 'icomoon' !important;

    &:empty {
      visibility: visible;
      width: 35px;
      content: none;
    }

    &:after {
      content: none;
    }

    ${({ $icons, $selected = false, $disabled = false }) => {
      const styles = getTypedStyle(ButtonType.GHOST);
      return `
        background-color: ${styles.backgroundColor};
        color: ${styles.color};
        border: none;

        &::before {
          content: '${$selected ? $icons[1] : $icons[0]}';
          display: block;
          position: static;
          background: transparent;
          opacity: 1;
          font-size: 20px;
        }

        ${
          $disabled
            ? `
          cursor: not-allowed;
          pointer-events: none;
          background-color: ${styles.disabled.backgroundColor};
          color: ${styles.disabled.color};
          border: ${styles.disabled.border};

          &:active,
          &:focus,
          &:hover {
            color: ${styles.disabled.color};
            background-color: var(--color-background);
          }
        `
            : `
          &:hover {
            background-color: var(--color-background);
            color: ${styles.hover.color};
            border: none;

            &:active,
            &:focus,
            &::before {
              content: '${$selected ? $icons[0] : $icons[1]}';
            }
          }
        `
        }
      `;
    }}
  }
`;

export const IconButton = styled(AntButton)<{
  $icon: string;
  $disabled?: boolean;
  $comingSoon?: boolean;
}>`
  &&& {
    width: 35px;
    height: 35px;
    border-radius: 35px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    white-space: nowrap;
    box-shadow: none;
    font-family: 'icomoon' !important;

    &:empty {
      visibility: visible;
      width: 35px;
      content: none;
    }

    ${({ $icon, $disabled = false, children }) => {
      const styles = getTypedStyle(ButtonType.GHOST);
      return `
        background-color: ${styles.backgroundColor};
        color: ${styles.color};
        border: none;

        &::after {
          content: '${$icon}';
          display: block;
          position: static;
          background: transparent;
          opacity: 1;
          font-size: 20px;
          color: inherit;
        }

        ${
          $disabled
            ? `
          cursor: not-allowed;
          pointer-events: none;
          background-color: ${
            !!children
              ? 'var(--color-background)'
              : styles.disabled.backgroundColor
          };
          color: ${
            !!children ? 'var(--color-secondary)' : styles.disabled.color
          };
          border: ${styles.disabled.border};

          &:active,
          &:focus,
          &:hover {
            color: ${styles.disabled.color};
            background-color: var(--color-background);
          }
        `
            : `
          &:active,
          &:focus,
          &:hover {
            background-color: var(--color-background);
            color: ${styles.hover.color};
            border: none;
          }
        `
        }

        ${
          !!children &&
          `
          width: auto;
          background-color: var(--color-background);
          border-radius: 21px;
          padding: 0 12px;
          color: var(--color-secondary);

          span {
            font-family: 'Manrope', sans-serif;
            font-weight: 700;
            font-size: 10px;
            line-height: 14px;
            text-align: center;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--color-secondary);
            margin-right: 4px;
          }
        `
        }
      `;
    }}
  }
`;

export const IconLinkButton = styled.a<{
  $width?: number;
  $type?: ButtonType;
  $disabled?: boolean;
}>`
  ${({ $width = 35 }) => `
  width: ${$width}px;
  height: ${$width}px;
  `}
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-white);
  transition: all 0.3s ease;
  white-space: nowrap;

  ::after {
    display: none;
  }

  ${({ $type = ButtonType.PRIMARY, $disabled = false }) => {
    const styles = getTypedStyle($type);
    const btnStyles = getTypedStyle(
      $type === ButtonType.PRIMARY ? ButtonType.OUTLINE : ButtonType.PRIMARY
    );

    return `
      background-color: ${styles.backgroundColor};
      color: ${styles.color};
      border: ${styles.border || 'none'};
      ${
        $disabled
          ? `
        cursor: not-allowed;
        pointer-events: none;
        background-color: ${styles.disabled.backgroundColor};
        color: ${styles.disabled.color};
        border: ${styles.disabled.border};
        &:hover {
          color: ${styles.disabled.color};
        }
      `
          : `
        &:hover {
          background-color: ${btnStyles.backgroundColor};
          color: ${btnStyles.color};
          border: ${btnStyles.border};
        }
      `
      }
    `;
  }}
`;
